import React, { useState, useEffect, useCallback, memo } from "react";
import {
  Row,
  Col,
  Card,
  Button,
  Modal,
  Form,
  Input,
  InputNumber,
  DatePicker,
  Popconfirm,
  message,
  Spin,
  Empty,
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  CalendarOutlined,
  CopyOutlined,
  PrinterOutlined,
  PrinterFilled,
} from "@ant-design/icons";

import { FaLightbulb } from "react-icons/fa6";

import { HiScissors } from "react-icons/hi2";
import { LuCookingPot } from "react-icons/lu";
import { IoInformationCircleOutline } from "react-icons/io5";
import { rtdb, app } from "../firebase";
import { ref, onValue, set, remove } from "firebase/database";
import dayjs from "dayjs";
import { useAuth } from "../context/AuthContext";

const formatNumber = (num) => {
  if (num === undefined || num === null) return "000";
  const n = typeof num === 'string' ? parseInt(num, 10) : num;
  if (isNaN(n)) return "000";
  return n.toString().padStart(3, '0');
};

// Composant pour afficher un texte tronqué avec "Voir plus"
const TruncatedText = memo(({ text, maxLength = 150 }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  
  if (!text || text.trim() === '') {
    return <span style={{ color: '#666', fontStyle: 'italic' }}>Aucune description</span>;
  }
  
  const shouldTruncate = text.length > maxLength;
  const displayText = isExpanded || !shouldTruncate ? text : `${text.substring(0, maxLength)}...`;
  
  return (
    <div>
      <span className="truncated-text">
        {displayText}
      </span>
      {shouldTruncate && (
        <Button
          type="link"
          size="small"
          onClick={() => setIsExpanded(!isExpanded)}
          style={{ 
            padding: '0',
            marginLeft: '4px',
            color: '#1677ff',
            fontSize: '12px'
          }}
        >
          {isExpanded ? 'Voir moins' : 'Voir plus'}
        </Button>
      )}
    </div>
  );
});

const Plantation = () => {
  const { selectedGreenhouse } = useAuth();
  const [plants, setPlants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingKey, setEditingKey] = useState(null);
  const [isDuplicating, setIsDuplicating] = useState(false);
  const [form] = Form.useForm();

  // 1. Récupération des données
  useEffect(() => {
    // Réinitialiser l'état si aucune serre n'est sélectionnée
    if (!selectedGreenhouse) {
      setPlants([]);
      setLoading(false);
      return;
    }

    const plantsRef = ref(rtdb, `greenhouses/${selectedGreenhouse}/plants`);
    const unsubscribe = onValue(plantsRef, (snapshot) => {
      const data = snapshot.val();
      // On convertit l'objet ou le tableau Firebase en liste exploitable
      const plantsList = data
        ? Object.entries(data).map(([id, val]) => ({ id, ...val }))
        : [];
      // Tri par numéro d'emplacement croissant
      plantsList.sort((a, b) => (a.number || 0) - (b.number || 0));
      setPlants(plantsList);
      setLoading(false);
    });
    return () => unsubscribe();
  }, [selectedGreenhouse]);

  // Fonction pour trouver le prochain numéro disponible
  const findNextAvailableNumber = () => {
    if (plants.length === 0) return 1;
    
    // Récupérer tous les numéros existants
    const existingNumbers = plants
      .map(p => typeof p.number === 'string' ? parseInt(p.number, 10) : p.number)
      .filter(n => !isNaN(n) && n >= 1 && n <= 999)
      .sort((a, b) => a - b);
    
    // Si aucun numéro valide, retourner 1
    if (existingNumbers.length === 0) return 1;
    
    // Chercher le premier trou dans la séquence
    for (let i = 1; i <= 999; i++) {
      if (!existingNumbers.includes(i)) {
        return i;
      }
    }
    
    // Si tous les numéros de 1 à 999 sont pris, retourner le plus grand + 1
    // (même si ça dépasse 999, on laissera la validation gérer l'erreur)
    return Math.max(...existingNumbers) + 1;
  };

  // 2. Ouvrir le modal (Ajout, Edit ou Duplication)
  const openModal = (plant = null, isDuplicate = false) => {
    setIsDuplicating(isDuplicate);
    
    if (plant) {
      if (isDuplicate) {
        // Pour la duplication, on garde toutes les données sauf le numéro
        // On suggère le prochain numéro disponible
        setEditingKey(null);
        form.setFieldsValue({
          number: findNextAvailableNumber(),
          variety: plant.variety,
          planting_date: dayjs(plant.planting_date),
          planting_description: plant.planting_description || null,
          url: plant.url || '', // Inclure l'URL si elle existe
        });
      } else {
        // Pour l'édition, on garde toutes les données
        setEditingKey(plant.id);
        form.setFieldsValue({
          ...plant,
          planting_date: dayjs(plant.planting_date),
        });
      }
    } else {
      // Pour un nouvel ajout, on suggère le prochain numéro disponible
      setEditingKey(null);
      form.setFieldsValue({
        number: findNextAvailableNumber(),
      });
    }
    setIsModalOpen(true);
  };

  // 3. Sauvegarder (Ajout ou Update)
  const handleSave = async () => {
    try {
      if (!selectedGreenhouse) {
        message.error("Veuillez sélectionner une serre");
        return;
      }

      const values = await form.validateFields();
      const id = editingKey || Date.now().toString(); // Génère un ID si c'est un nouvel ajout
      
      // InputNumber nous donne déjà un nombre, on s'assure que c'est un entier
      const numberValue = Math.floor(values.number);
      
      const plantData = {
        number: numberValue, // Stocké comme entier
        variety: values.variety,
        planting_date: values.planting_date.format("YYYY-MM-DD"),
        planting_description: values.planting_description || null,
        url: values.url || null, // URL facultative
      };

      await set(ref(rtdb, `greenhouses/${selectedGreenhouse}/plants/${id}`), plantData);
      message.success(
        editingKey ? "Plantation mise à jour" : "Nouvelle plante ajoutée",
      );
      setIsModalOpen(false);
      form.resetFields(); // Réinitialiser le formulaire après sauvegarde
    } catch (error) {
      console.error(error);
    }
  };

  // État pour le modal de recette IA
  const [recipeModalVisible, setRecipeModalVisible] = useState(false);
  const [recipeContent, setRecipeContent] = useState("");
  const [recipeLoading, setRecipeLoading] = useState(false);
  const [recipePlantName, setRecipePlantName] = useState("");
  const [recipeTitle, setRecipeTitle] = useState("");

  // État pour le modal de choix IA
  const [iaChoiceModalVisible, setIaChoiceModalVisible] = useState(false);
  const [iaChoicePlant, setIaChoicePlant] = useState(null);

  // 5. Appel à DeepSeek pour l'IA
  const callPlantAI = useCallback(async (plant, type) => {
    setIaChoiceModalVisible(false);
    setRecipePlantName(plant.variety);
    setRecipeContent("");

    const titles = {
      recipe: "Recette",
      harvest: "Récolte & Conservation",
      information: "Informations",
    };
    setRecipeTitle(titles[type] || "IA");

    const functionNames = {
      recipe: "getPlantRecipe",
      harvest: "getPlantHarvest",
      information: "getPlantInformation",
    };
    const resultKeys = {
      recipe: "recipe",
      harvest: "advice",
      information: "information",
    };

    setRecipeModalVisible(true);
    setRecipeLoading(true);

    try {
      const { getFunctions, httpsCallable } = await import("firebase/functions");
      const functions = getFunctions(app, "europe-west1");
      const callable = httpsCallable(functions, functionNames[type]);
      const result = await callable({ plant: plant.variety });
      setRecipeContent(result.data[resultKeys[type]]);
    } catch (error) {
      console.error(error);
      message.error("Erreur lors de l'appel à l'IA");
      setRecipeContent("Erreur : impossible de générer le contenu.");
      setRecipeModalVisible(true);
    } finally {
      setRecipeLoading(false);
    }
  }, []);

  // Génère le HTML d'une étiquette pour une plante
  const buildLabelHtml = (plant) => `
    <div class="label">
      <div class="label-number">N° ${formatNumber(plant.number)}</div>
      <div class="label-name">${plant.variety}</div>
      <div class="label-date">Date de plantation : ${plant.planting_date || ""}</div>
    </div>
  `;

  // Ouvre une fenêtre d'impression avec le HTML des étiquettes
  const openPrintWindow = (title, labelsHtml) => {
    const printWindow = window.open("", "_blank");
    if (!printWindow) {
      message.error("Veuillez autoriser les popups pour imprimer");
      return;
    }

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>${title}</title>
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body { font-family: Arial, Helvetica, sans-serif; color: #000; }
          .page {
            width: 210mm;
            height: 297mm;
            padding: 10mm;
            page-break-after: always;
          }
          .page:last-child { page-break-after: auto; }
          .grid {
            display: grid;
            grid-template-columns: 1fr 1fr;
          }
          .label {
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            padding: 1mm;
            text-align: center;
            height: 54mm;
            page-break-inside: avoid;
            border: 1px dashed #bbb;
          }
          .grid { margin: -0.5px; }
          .label-number {
            font-size: 22pt;
            font-weight: bold;
            color: #999;
            margin-bottom: 2mm;
          }
          .label-name {
            font-size: 16pt;
            font-weight: bold;
            line-height: 1.2;
          }
          .label-date {
            font-size: 11pt;
            margin-top: 2mm;
            color: #333;
          }
          @media print {
            body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          }
        </style>
      </head>
      <body>
        ${labelsHtml}
      </body>
      </html>
    `);

    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => printWindow.print(), 500);
  };

  // Imprimer toutes les étiquettes (planche)
  const handlePrintLabels = () => {
    if (plants.length === 0) {
      message.warning("Aucune plante à imprimer");
      return;
    }

    // Générer les étiquettes par groupes de 10
    const chunks = [];
    for (let i = 0; i < plants.length; i += 10) {
      chunks.push(plants.slice(i, i + 10));
    }

    const labelsHtml = chunks
      .map(
        (chunk) => `
      <div class="page">
        <div class="grid">
          ${chunk.map((plant) => buildLabelHtml(plant)).join("")}
        </div>
      </div>
    `
      )
      .join("");

    openPrintWindow("Étiquettes - Plantations", labelsHtml);
  };

  // Imprimer une seule étiquette
  const handlePrintSingleLabel = (plant) => {
    const labelsHtml = `
      <div class="page">
        <div class="grid">
          ${buildLabelHtml(plant)}
        </div>
      </div>
    `;
    openPrintWindow(`Étiquette - ${plant.variety}`, labelsHtml);
  };


  // 4. Supprimer
  const handleDelete = async (id) => {

    try {
      if (!selectedGreenhouse) {
        message.error("Veuillez sélectionner une serre");
        return;
      }

      await remove(ref(rtdb, `greenhouses/${selectedGreenhouse}/plants/${id}`));
      message.success("Plante supprimée");
    } catch {
      message.error("Erreur lors de la suppression");
    }
  };

  if (loading)
    return (
      <div className="loading-container">
        <Spin size="large" />
      </div>
    );

  return (
    <div className="page-container">
      <div style={{ marginBottom: 24 }}>
        <h2 className="page-title">
          Gestion des Plantations
        </h2>
        <Button
          block
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => openModal()}
        >
          Ajouter une plante
        </Button>
        <Button
          block
          icon={<PrinterOutlined />}
          onClick={handlePrintLabels}
          className="btn-print"
        >
          Imprimer les étiquettes
        </Button>
      </div>


      {plants.length === 0 ? (
        <Empty
          description={
            <span style={{ color: "#666" }}>Aucune plante enregistrée</span>
          }
        />
      ) : (
        <Row gutter={[16, 16]}>
          {plants.map((plant) => (
            <Col xs={24} sm={12} md={12} key={plant.id}>
              <Card
                title={
                  <div className="card-title-flex" style={{ justifyContent: "space-between" }}>
                    <span>N° {formatNumber(plant.number)} - {plant.variety}</span>
                    <Button
                      type="link"
                      size="small"
                      icon={<FaLightbulb style={{color:"#fd0"}} />}
                      onClick={(e) => {
                        e.stopPropagation();
                        setIaChoicePlant(plant);
                        setIaChoiceModalVisible(true);
                      }}
                      style={{ color: "#52c41a", padding: "0 4px" }}
                      title="Obtenir une recette avec l'IA"
                    />
                  </div>
                }
                className="card-style"
                classNames={{ header: "card-header-style", body: "card-body-style" }}
                actions={[
                  <PrinterFilled
                    key="print"
                    style={{ color: "#ccc" }}
                    onClick={() => handlePrintSingleLabel(plant)}
                    title="Imprimer l'étiquette"
                  />,
                  <CopyOutlined
                    key="duplicate"
                    style={{ color: "#52c41a" }}
                    onClick={() => openModal(plant, true)}
                    title="Dupliquer cette plante"
                  />,
                  <EditOutlined
                    key="edit"
                    style={{ color: "#1677ff" }}
                    onClick={() => openModal(plant)}
                    title="Modifier cette plante"
                  />,
                  <Popconfirm
                    title="Supprimer cette plante ?"
                    onConfirm={() => handleDelete(plant.id)}
                    okText="Oui"
                    cancelText="Non"
                  >
                    <DeleteOutlined 
                      key="delete" 
                      style={{ color: "#ff4d4f" }}
                      title="Supprimer cette plante"
                    />
                  </Popconfirm>,
                ]}

              >
                <div style={{ color: "var(--text-medium)", marginTop: 8 }}>
                  <p>
                    <CalendarOutlined /> Planté le : {plant.planting_date}
                    {plant.url && (
                      <>
                        {" | "}
                        <a 
                          href={plant.url} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          style={{ color: "var(--color-primary)", textDecoration: "none", display: "inline" }}
                        >
                          🌐 Lien web
                        </a>
                      </>
                    )}
                  </p>

                  <div className="plant-description-box">
                    <TruncatedText text={plant.planting_description} maxLength={40} />
                  </div>
                </div>
              </Card>
            </Col>
          ))}
        </Row>
      )}

      {/* MODAL AJOUT / EDITION */}
      <Modal
        title={
          editingKey ? "Modifier la plante" : 
          isDuplicating ? "Dupliquer la plante" : 
          "Ajouter une plante"
        }
        open={isModalOpen}
        onOk={handleSave}
        onCancel={() => {
          setIsModalOpen(false);
          form.resetFields(); // Réinitialiser le formulaire lors de l'annulation
        }}
        okText="Enregistrer"
        cancelText="Annuler"
        destroyOnClose
      >
        <Form form={form} layout="vertical" style={{ marginTop: 20 }}>
          <Form.Item
            name="number"
            label="Numéro de l'emplacement"
            rules={[
              { required: true, message: "Le numéro est requis" },
              () => ({
                validator(_, value) {
                  if (!value && value !== 0) {
                    return Promise.reject(new Error("Le numéro est requis"));
                  }
                  const num = Number(value);
                  if (isNaN(num)) {
                    return Promise.reject(new Error("Le numéro doit être un nombre"));
                  }
                  if (!Number.isInteger(num)) {
                    return Promise.reject(new Error("Le numéro doit être un entier"));
                  }
                  if (num < 1) {
                    return Promise.reject(new Error("Le numéro doit être supérieur à 0"));
                  }
                  if (num > 999) {
                    return Promise.reject(new Error("Le numéro doit être inférieur à 1000"));
                  }
                  return Promise.resolve();
                },
              }),
            ]}
          >
            <InputNumber 
              min={1}
              max={999}
              style={{ width: '100%' }}
              placeholder="ex: 1 (sera affiché comme 001)"
            />
          </Form.Item>
          <Form.Item
            name="variety"
            label="Variété"
            rules={[{ required: true }]}
          >
            <Input placeholder="ex: Laitue Romaine" />
          </Form.Item>
          <Form.Item
            name="planting_date"
            label="Date de plantation"
            rules={[{ required: true }]}
          >
            <DatePicker style={{ width: "100%" }} format="YYYY-MM-DD" />
          </Form.Item>
          <Form.Item
            name="planting_description"
            label="Description de la plantation"
            rules={[{ required: false }]}
          >
            <Input.TextArea placeholder="description sommaire de la plante" rows={4} />
          </Form.Item>
          <Form.Item
            name="url"
            label="Lien web (facultatif)"
            rules={[
              {
                type: 'url',
                message: 'Veuillez entrer une URL valide (ex: https://exemple.com)',
              },
            ]}
          >
            <Input 
              placeholder="https://exemple.com" 
              addonBefore="🌐"
            />
          </Form.Item>
        </Form>
      </Modal>

      {/* MODAL CHOIX IA */}
      <Modal
        title={<><FaLightbulb style={{color:"#fd0"}} /> Que faire avec {iaChoicePlant?.variety || ""} ?</>}

        open={iaChoiceModalVisible}
        onCancel={() => setIaChoiceModalVisible(false)}
        footer={null}
        width={400}
        destroyOnClose
      >
        <div style={{ display: "flex", flexDirection: "column", gap: 12, marginTop: 16 }}>
          <Button
            size="large"
            block
            className="btn-ia-choice"
            onClick={() => iaChoicePlant && callPlantAI(iaChoicePlant, "recipe")}
          >
            <LuCookingPot style={{ fontSize: 20 }} /> Recette
          </Button>
          <Button
            size="large"
            block
            className="btn-ia-choice"
            onClick={() => iaChoicePlant && callPlantAI(iaChoicePlant, "harvest")}
          >
            <HiScissors style={{ fontSize: 20 }} /> Récolte & Conservation
          </Button>
          <Button
            size="large"
            block
            className="btn-ia-choice"
            onClick={() => iaChoicePlant && callPlantAI(iaChoicePlant, "information")}
          >
            <IoInformationCircleOutline style={{ fontSize: 20 }} /> Informations
          </Button>
        </div>
      </Modal>

      {/* MODAL IA RESULTAT */}
      <Modal
        title={<><FaLightbulb style={{color:"#fd0"}} /> {recipeTitle} - {recipePlantName}</>}

        open={recipeModalVisible}
        onCancel={() => setRecipeModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setRecipeModalVisible(false)}>
            Fermer
          </Button>,
        ]}
        width={600}
        destroyOnClose
      >
        {recipeLoading ? (
          <div style={{ textAlign: "center", padding: "40px 0" }}>
            <Spin size="large" />
            <p style={{ color: "#aaa", marginTop: 16 }}>
              Génération en cours...
            </p>
          </div>
        ) : (
          <div
            style={{
              color: "#ccc",
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
              lineHeight: 1.6,
              maxHeight: "60vh",
              overflowY: "auto",
            }}
          >
            {recipeContent}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default Plantation;
